import { BACKEND_URL } from '../cpts/Form/CoreCpts/lib/urls';

export interface AuthProvider {
  isAuthenticated(): boolean;
  getAuthHeaders(): Record<string, string>;
}

let authProvider: AuthProvider | null = null;

export function setAuthProvider(provider: AuthProvider | null) {
  authProvider = provider;
}

// Event dispatcher for session expiry
export const sessionExpiredEvent = new EventTarget();

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * API interceptor that handles all HTTP requests with automatic JWT token injection
 * and session expiry detection. When a 401 error is received, it dispatches a
 * session expired event that can be handled by the UI.
 */
export class ApiInterceptor {
  private static instance: ApiInterceptor;

  private constructor() {}

  public static getInstance(): ApiInterceptor {
    if (!ApiInterceptor.instance) {
      ApiInterceptor.instance = new ApiInterceptor();
    }
    return ApiInterceptor.instance;
  }

  /**
   * Make an HTTP request with automatic authentication and error handling
   */
  public async request(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    const { skipAuth = false, ...fetchOptions } = options;

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers as Record<string, string>,
    };

    // Add authentication headers if not skipped and user is authenticated
    if (!skipAuth && authProvider?.isAuthenticated()) {
      Object.assign(headers, authProvider.getAuthHeaders());
    }

    // Make the request
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Handle 401 Unauthorized - Session expired
    if (response.status === 401) {
      console.warn('Session expired - dispatching session expired event');
      sessionExpiredEvent.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: {
          url,
          status: response.status,
          statusText: response.statusText
        }
      }));
    }

    return response;
  }

  /**
   * Convenience method for GET requests
   */
  public async get(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * Convenience method for POST requests
   */
  public async post(url: string, data?: any, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Convenience method for PUT requests
   */
  public async put(url: string, data?: any, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  public async delete(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, { ...options, method: 'DELETE' });
  }

  /**
   * Convenience method for PATCH requests
   */
  public async patch(url: string, data?: any, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// Export singleton instance
export const apiInterceptor = ApiInterceptor.getInstance();

/**
 * Resolve `url` (absolute, protocol-relative, or relative) against `base` using real URL
 * semantics, and return its origin -- or `null` when no http(s) origin can be established.
 *
 * `null` is deliberately the FAIL-CLOSED answer: every caller treats "origin unknown" as
 * EXTERNAL and therefore sends no Authorization header. Handing our JWT to an origin we
 * could not even parse is far worse than an unauthenticated request that 401s visibly.
 *
 * Non-http(s) schemes (`data:`, `blob:`, `javascript:`, `mailto:`, extension schemes, ...)
 * return `null` for the same reason: they are never our API, and some of them report a
 * borrowed or opaque origin (`blob:https://our-host/...` borrows our origin, `data:` reports
 * `"null"`) that must not be allowed to pass as same-origin.
 */
function httpOriginOf(url: string, base?: string): string | null {
  try {
    const resolved = new URL(url, base);
    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') {
      return null;
    }
    return resolved.origin;
  } catch {
    // Unparseable: garbage input, or a relative URL with no usable base.
    return null;
  }
}

/**
 * Is `url` outside our trust boundary -- i.e. must it NOT receive our Authorization header?
 *
 * INTERNAL (token attached) means exactly two things, compared by HOST:
 *   1. the page's own origin -- which covers every relative URL, the normal case; or
 *   2. the origin of the configured backend (`VITE_BACKEND_URL` via `BACKEND_URL`), if one is
 *      configured at all (it defaults to `''`, i.e. "same origin, relative URLs").
 * Everything else is EXTERNAL: third-party hosts, protocol-relative `//other-host/...`,
 * non-http(s) schemes, and anything unparseable.
 *
 * This is a host comparison, NOT a scheme test. The previous implementation asked
 * `url.startsWith('http://') || url.startsWith('https://')`, which classified our own
 * absolute API URLs (`https://our-own-host/api/...`) as external and silently dropped their
 * Authorization header -- latent only because `BACKEND_URL` defaults to `''`, so every API
 * call is currently relative.
 *
 * Matching is exact origin equality (scheme + host + port). No suffix or domain matching, so
 * a look-alike host such as `our-host.attacker.example` can never be mistaken for ours, and
 * `http://` never passes as the `https://` origin of the same host.
 */
export function isExternalUrl(url: string, backendUrl: string = BACKEND_URL): boolean {
  // No browsing context => no origin to compare against => fail closed.
  const pageHref = typeof window === 'undefined' ? undefined : window.location.href;
  const pageOrigin = pageHref ? httpOriginOf(pageHref) : null;

  const targetOrigin = httpOriginOf(url, pageHref);
  if (targetOrigin === null) {
    return true;
  }

  if (pageOrigin !== null && targetOrigin === pageOrigin) {
    return false;
  }

  // An explicitly configured backend is ours by definition. `''` means "unset" -- never
  // resolve it, or it would collapse to the page origin and add nothing.
  if (backendUrl) {
    const backendOrigin = httpOriginOf(backendUrl, pageHref);
    if (backendOrigin !== null && targetOrigin === backendOrigin) {
      return false;
    }
  }

  return true;
}

/**
 * Monkey-patch the global fetch() to automatically add Authorization headers
 * This ensures ALL fetch requests in the app get authenticated automatically
 */
export function setupFetchInterceptor() {
  const originalFetch = window.fetch;

  window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Convert input to URL string
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    // Skip authentication for URLs outside our own origin / configured backend origin (the OTP
    // auth backend at OTP_AUTH_CONFIG.BASE_URL is one such external host: authService signs its
    // own requests there explicitly, this interceptor must not).
    const skipAuth = (init?.headers && 'X-Skip-Auth' in init.headers) || isExternalUrl(url);

    // Add Authorization header if authenticated and not skipped
    if (!skipAuth && authProvider?.isAuthenticated()) {
      const authHeaders = authProvider.getAuthHeaders();

      // Merge headers
      const headers = new Headers(init?.headers);
      Object.entries(authHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });

      init = {
        ...init,
        headers,
      };
    }

    // Make the original fetch request
    const response = await originalFetch(input, init);

    // Handle 401 Unauthorized - Session expired
    if (response.status === 401) {
      console.warn('Session expired (from intercepted fetch) - dispatching event');
      sessionExpiredEvent.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: {
          url,
          status: response.status,
          statusText: response.statusText
        }
      }));
    }

    return response;
  };

  console.log('[apiInterceptor] Global fetch() interceptor installed');
}

// Auto-setup on module load
setupFetchInterceptor();